/**
 * pre-trim-modal.js — shared "trim before encode" gate for video tools.
 *
 * Why this exists:
 *   Files >150 MB blew the WASM-x264 budget on every re-encode tool —
 *   browser-side encode of a 1080p screencast routinely takes 30+ min, so
 *   users hit Stop / close the tab. The 2026-04-28 bug-report (sid
 *   2211c8f7-...) on enhance-video — 472 MB MP4, "Не грузит дальше 11 %"
 *   — was the prototype. Telemetry shows the same shape on add-music-to-
 *   video, video-crop, video-flip, reels-maker, video-speed, etc.
 *
 *   Pre-trim presents an inline modal *before* workspace activates: native
 *   <video> preview + double-handle range slider, default 0..60 s. User
 *   clips first via FFmpeg `-c copy` (sub-second on 500 MB, no re-encode),
 *   trimmed File replaces the original, downstream pipeline never sees the
 *   big blob.
 *
 * API:
 *
 *   window.PreTrimModal.show(file, opts) → Promise<File | null>
 *
 *   opts.threshold  — bytes; modal skipped if file.size < threshold (default 150 MB)
 *   opts.defaultLen — initial selection in seconds (default 60)
 *   opts.slug       — tool slug for telemetry tagging (required for tagged events)
 *
 *   Resolves to:
 *     null          — user clicked Cancel; caller should reset uploader
 *     trimmed File  — user clicked "Trim and continue"; replaces the original
 *     original File — user clicked "Process whole file" or probing failed
 *
 * Caller integration in handleFile():
 *
 *   if (window.PreTrimModal) {
 *       var trimmed = await PreTrimModal.show(file, { slug: 'video-crop' });
 *       if (trimmed === null) { resetUploader(); return; }
 *       file = trimmed;
 *   }
 *
 *   Tools with copy-mode pipelines (video-trim, mute-video) or specialty
 *   pipelines (repair-video, video-compressor) should NOT call this — pre-
 *   trim contradicts their product intent or is redundant.
 *
 * Dependencies:
 *   - window.FFmpegProcessor (for the trim itself)
 *   - window.__Mp4BoxProbe (optional; HTML5 metadata is the fallback)
 *   - window.__toast (optional; trim failures fall back to original w/ toast)
 *   - window.__ae (optional; telemetry events)
 *   - window.__LOCALE (optional; falls back to 'en')
 */
(function(){
'use strict';
if (window.PreTrimModal) return;

var DEFAULT_THRESHOLD = 150 * 1024 * 1024;
var DEFAULT_LEN = 60;

// ─── i18n ─────────────────────────────────────────────────────────────
// Inline locale tables — same pattern the rest of the codebase uses
// (timbrica-core.js, ad-gate, etc). Keeps the module self-contained;
// tools that already have these keys in their per-tool lang file can pass
// `opts.t` to override.
var I18N = {
    title: {
        en: 'Large file — trim first',
        ru: 'Большой файл — обрежьте сначала',
        ko: '대용량 파일 — 먼저 자르세요',
        ar: 'ملف كبير — قُص أولاً',
        id: 'File besar — pangkas dulu',
        pt: 'Arquivo grande — corte primeiro',
        de: 'Große Datei — zuerst zuschneiden',
        es: 'Archivo grande — recorta primero',
        fr: 'Fichier volumineux — coupez d’abord', uk: 'Великий файл — спочатку обріжте',
        tr: 'Büyük dosya — önce kırpın'
    },
    explain: {
        en: 'File is {size} MB ({dur}). Browser-side processing of this size takes a long time. Trim to the part you actually need — it is fast and lossless (no re-encode).',
        ru: 'Файл {size} МБ ({dur}). Браузерная обработка такого размера занимает много времени. Обрежьте до нужного фрагмента — это быстро и без потерь (без перекодирования).',
        ko: '파일 크기 {size} MB ({dur}). 이 크기의 브라우저 처리는 오래 걸립니다. 필요한 부분만 잘라내세요 — 빠르고 무손실(재인코딩 없음)입니다.',
        ar: 'حجم الملف {size} ميجابايت ({dur}). معالجة هذا الحجم في المتصفح تستغرق وقتاً طويلاً. قُص الجزء الذي تحتاجه فقط — سريع وبدون فقدان جودة (بدون إعادة ترميز).',
        id: 'Ukuran file {size} MB ({dur}). Pemrosesan ukuran ini di browser memakan waktu lama. Pangkas hanya bagian yang Anda butuhkan — cepat dan tanpa kehilangan kualitas (tanpa encoding ulang).',
        pt: 'Arquivo de {size} MB ({dur}). O processamento desse tamanho no navegador leva muito tempo. Corte apenas a parte de que precisa — é rápido e sem perda (sem recodificação).',
        de: 'Datei {size} MB ({dur}). Browser-Verarbeitung dieser Größe dauert sehr lange. Schneiden Sie nur den benötigten Teil — schnell und verlustfrei (ohne Neukodierung).',
        es: 'El archivo pesa {size} MB ({dur}). Procesar este tamaño en el navegador lleva mucho tiempo. Recorta solo la parte que necesitas — es rápido y sin pérdida (sin recodificación).',
        fr: 'Le fichier fait {size} Mo ({dur}). Traiter cette taille dans le navigateur prend beaucoup de temps. Coupez uniquement la partie utile — rapide et sans perte (sans réencodage).',
        uk: 'Файл {size} МБ ({dur}). Браузерна обробка такого розміру триває довго. Обріжте до потрібної частини — це швидко й без втрат (без перекодування).',
        tr: 'Dosya {size} MB ({dur}). Bu boyuttaki bir dosyanın tarayıcıda işlenmesi uzun sürer. Yalnızca ihtiyacınız olan bölümü kırpın — hızlıdır ve kalite kaybı olmaz (yeniden kodlama yapılmaz).'
    },
    start:   { en: 'Start',  ru: 'Начало', ko: '시작',   ar: 'البداية', id: 'Mulai', pt: 'Início', de: 'Start', es: 'Inicio', fr: 'Début', uk: 'Початок', tr: 'Başlangıç' },
    end:     { en: 'End',    ru: 'Конец',  ko: '끝',     ar: 'النهاية', id: 'Akhir', pt: 'Fim',    de: 'Ende', es: 'Fin', fr: 'Fin', uk: 'Кінець', tr: 'Bitiş' },
    len:     { en: 'Length', ru: 'Длина',  ko: '길이',   ar: 'المدة',   id: 'Durasi',pt: 'Duração',de: 'Länge', es: 'Duración', fr: 'Durée', uk: 'Довжина', tr: 'Süre' },
    confirm: {
        en: 'Trim and continue',
        ru: 'Обрезать и продолжить',
        ko: '잘라서 계속하기',
        ar: 'قُص وتابع',
        id: 'Pangkas dan lanjutkan',
        pt: 'Cortar e continuar',
        de: 'Zuschneiden und fortfahren',
        es: 'Recortar y continuar',
        fr: 'Couper et continuer', uk: 'Обрізати і продовжити',
        tr: 'Kırp ve devam et'
    },
    skip: {
        en: 'Process whole file',
        ru: 'Обрабатывать целиком',
        ko: '전체 처리',
        ar: 'معالجة الملف كاملاً',
        id: 'Proses seluruh file',
        pt: 'Processar arquivo inteiro',
        de: 'Ganze Datei verarbeiten',
        es: 'Procesar archivo completo',
        fr: 'Traiter le fichier entier', uk: 'Обробляти повністю',
        tr: 'Tüm dosyayı işle'
    },
    cancel: { en: 'Cancel', ru: 'Отмена', ko: '취소', ar: 'إلغاء', id: 'Batal', pt: 'Cancelar', de: 'Abbrechen', es: 'Cancelar', fr: 'Annuler', uk: 'Скасувати', tr: 'İptal' },
    busy:   { en: 'Trimming...', ru: 'Обрезаем...', ko: '자르는 중...', ar: 'جاري القص...', id: 'Memangkas...', pt: 'Cortando...', de: 'Schneide zu...', es: 'Recortando...', fr: 'Découpe en cours...', uk: 'Обрізаємо...', tr: 'Kırpılıyor...' },
    too_short: {
        en: 'Fragment too short — pick at least 1 second',
        ru: 'Слишком короткий фрагмент — выберите хотя бы 1 секунду',
        ko: '구간이 너무 짧습니다 — 최소 1초 선택',
        ar: 'الجزء قصير جداً — اختر ثانية واحدة على الأقل',
        id: 'Fragmen terlalu pendek — pilih minimal 1 detik',
        pt: 'Trecho muito curto — selecione pelo menos 1 segundo',
        de: 'Fragment zu kurz — mindestens 1 Sekunde wählen',
        es: 'Fragmento demasiado corto — elige al menos 1 segundo',
        fr: 'Fragment trop court — choisissez au moins 1 seconde', uk: 'Фрагмент занадто короткий — виберіть хоча б 1 секунду',
        tr: 'Seçili aralık çok kısa — en az 1 saniye seçin'
    },
    err: {
        en: 'Could not trim. Continuing with the original file.',
        ru: 'Не удалось обрезать. Продолжаем с оригиналом.',
        ko: '자를 수 없습니다. 원본으로 계속합니다.',
        ar: 'تعذر القص. نتابع بالملف الأصلي.',
        id: 'Tidak dapat memangkas. Melanjutkan dengan file asli.',
        pt: 'Não foi possível cortar. Continuando com o original.',
        de: 'Zuschneiden fehlgeschlagen. Fortsetzung mit dem Original.',
        es: 'No se pudo recortar. Continuamos con el archivo original.',
        fr: 'Impossible de couper. On continue avec le fichier original.', uk: 'Не вдалося обрізати. Продовжуємо з оригіналом.',
        tr: 'Kırpılamadı. Orijinal dosyayla devam ediliyor.'
    }
};
function tr(key) {
    var loc = (window.__LOCALE || 'en').substring(0, 2);
    var bag = I18N[key] || {};
    return bag[loc] || bag.en || key;
}

// ─── helpers ──────────────────────────────────────────────────────────
function fmtTime(s) {
    if (!s || isNaN(s) || s <= 0) return '0:00';
    var m = Math.floor(s / 60);
    var ss = Math.floor(s % 60);
    return m + ':' + String(ss).padStart(2, '0');
}

function emit(action, payload, slug) {
    if (!window.__ae) return;
    try {
        var p = Object.assign({ slug: slug || '', action: action }, payload || {});
        window.__ae('feature_use', p);
    } catch (_) {}
}

async function probeDuration(file) {
    // Fast path — mp4box probe for mp4-family containers (~100 ms, no FFmpeg).
    try {
        if (window.__Mp4BoxProbe && /\.(mp4|mov|m4v)$/i.test(file.name)) {
            var info = await Promise.race([
                window.__Mp4BoxProbe(file),
                new Promise(function(_, rej) { setTimeout(function() { rej(new Error('probe-timeout')); }, 4000); })
            ]);
            if (info && info.duration) return info.duration;
        }
    } catch (_) {}
    // HTML5 metadata fallback — works for anything browser-decodable
    try {
        var v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        var url = URL.createObjectURL(file);
        v.src = url;
        var d = await Promise.race([
            new Promise(function(res) { v.onloadedmetadata = function() { res(v.duration || 0); }; }),
            new Promise(function(res) { setTimeout(function() { res(0); }, 5000); })
        ]);
        try { URL.revokeObjectURL(url); } catch (_) {}
        return d || 0;
    } catch (_) { return 0; }
}

// Phase 4.B.1 — dispatch:
//   1. mp4-family → MP4BoxTrim (sub-second, no WASM, JS-only)
//   2. webm / mkv → WebmCopyTrim (same shape: EBML bookkeeping, no WASM; 2026-09-17)
//   3. anything else (avi/ts/wmv/...) → FFmpeg `-c copy`
// On MP4Box failure (unsupported codec, malformed atoms, etc) we still
// fall back to FFmpeg rather than surface the error — user got modal
// open, picked a range, and expects a result.
async function trimDispatch(file, startSec, endSec, slug) {
    var emitFn = function(action, payload){ emit(action, payload, slug); };
    if (window.MP4BoxTrim && window.MP4BoxTrim.isMp4Family(file)) {
        var t0 = Date.now();
        try {
            var out = await window.MP4BoxTrim.run(file, startSec, endSec);
            out.__trimVia = 'mp4box';
            emitFn('pretrim_path', { path: 'mp4box', ms: Date.now() - t0 });
            return out;
        } catch (mbErr) {
            // Telemetry: which codec/track-error fell through. Don't fail —
            // FFmpeg fallback below covers the long tail.
            emitFn('pretrim_mp4box_fallback', {
                err: ((mbErr && mbErr.message) || '').substring(0, 100),
                ms: Date.now() - t0
            });
        }
    }
    if (window.WebmCopyTrim && window.WebmCopyTrim.isWebm(file)) {
        var t1 = Date.now();
        try {
            var wout = await window.WebmCopyTrim.run(file, startSec, endSec);
            wout.__trimVia = 'webm-copy';
            emitFn('pretrim_path', { path: 'webm-copy', ms: Date.now() - t1 });
            return wout;
        } catch (wErr) {
            emitFn('pretrim_webm_fallback', { err: ((wErr && wErr.message) || '').substring(0, 100), code: (wErr && wErr.code) || '', ms: Date.now() - t1 });
        }
    }
    var ffOut = await trimViaFFmpegCopy(file, startSec, endSec);
    ffOut.__trimVia = 'ffmpeg';
    emitFn('pretrim_path', { path: 'ffmpeg' });
    return ffOut;
}

async function trimViaFFmpegCopy(file, startSec, endSec) {
    if (!window.FFmpegProcessor) throw new Error('ffmpeg-missing');
    var fp = window.FFmpegProcessor;
    if (!fp.isReady()) {
        var ok = await fp.init(function() {});
        if (!ok || !fp.isReady()) throw new Error('ffmpeg-init-fail');
    }
    if (fp.cacheFile) { try { await fp.cacheFile(file); } catch (_) {} }
    var ext = ((file.name || 'video.mp4').split('.').pop() || 'mp4').toLowerCase();
    var outName = 'trimmed.' + ext;
    var preArgs = ['-ss', startSec.toFixed(3)];
    var args = ['-t', (endSec - startSec).toFixed(3), '-c', 'copy',
                '-avoid_negative_ts', 'make_zero', '-movflags', '+faststart'];
    if (fp) fp.__nextOperation = 'copy';
    var result = await fp.process(file, args, outName, 'input.' + ext, preArgs);
    if (!result || !result.blob) throw new Error('empty-result');
    // The trim result is an INTERMEDIATE, not the tool's output —
    // process() already called URL.createObjectURL on this blob, and
    // telemetry's blob auto-tracker was firing a phantom tool_complete
    // (src:blob, application/octet-stream, ≈input size) ~2 s after every
    // pre-trim, poisoning the success funnel (2026-07-27 iOS report:
    // "complete" logged for a user who never exported anything).
    try { window.__skipBlobAutoTrack && window.__skipBlobAutoTrack(result.blob); } catch (_) {}
    var newName = (file.name || 'video.mp4').replace(/(\.[^.]+)?$/, '_trim$1');
    return new File([result.blob], newName, { type: file.type || 'video/mp4' });
}

// ─── styles, injected once ────────────────────────────────────────────
function ensureStyles() {
    if (document.getElementById('pre-trim-modal-styles')) return;
    var st = document.createElement('style');
    st.id = 'pre-trim-modal-styles';
    st.textContent =
        '.ptm-overlay{position:fixed;top:0;left:0;right:0;bottom:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.7);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);animation:ptm-fadein 160ms ease-out}' +
        '@keyframes ptm-fadein{from{opacity:0}to{opacity:1}}' +
        '.ptm-card{background:var(--bg-primary,#181b25);border:1px solid var(--border,#303547);border-radius:16px;padding:22px;width:92%;max-width:560px;box-shadow:0 20px 60px rgba(0,0,0,0.5)}' +
        '.ptm-title{font-size:18px;font-weight:700;color:var(--text-primary,#eef);margin-bottom:10px}' +
        '.ptm-msg{font-size:14px;color:var(--text-secondary,#aab);line-height:1.5;margin-bottom:14px}' +
        '.ptm-video{display:block;width:100%;max-height:200px;background:#000;border-radius:8px;margin-bottom:14px}' +
        '.ptm-rangewrap{position:relative;height:32px;margin:8px 0 4px;padding:0 6px}' +
        '.ptm-track{position:absolute;top:14px;left:6px;right:6px;height:4px;background:var(--border,#303547);border-radius:2px;pointer-events:none}' +
        '.ptm-range{position:absolute;top:0;left:0;right:0;width:100%;-webkit-appearance:none;appearance:none;background:transparent;pointer-events:none;height:32px}' +
        '.ptm-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:18px;height:18px;background:var(--accent,#4a90ff);border:2px solid #fff;border-radius:50%;cursor:pointer;pointer-events:auto;box-shadow:0 2px 8px rgba(0,0,0,0.3)}' +
        '.ptm-range::-moz-range-thumb{width:18px;height:18px;background:var(--accent,#4a90ff);border:2px solid #fff;border-radius:50%;cursor:pointer;pointer-events:auto}' +
        '.ptm-times{display:flex;flex-wrap:wrap;gap:14px;font-size:13px;color:var(--text-secondary,#aab);margin-bottom:14px}' +
        '.ptm-times b{color:var(--text-primary,#eef);font-weight:600}' +
        '.ptm-actions{display:flex;flex-direction:column;gap:8px}' +
        '.ptm-actions button{width:100%;padding:12px 16px;font-size:14px;border-radius:10px;cursor:pointer;font-family:inherit;font-weight:600;transition:transform 80ms ease,filter 80ms ease}' +
        '.ptm-actions button:hover{filter:brightness(1.07)}' +
        '.ptm-actions button:active{transform:translateY(1px)}' +
        '.ptm-confirm{background:var(--accent-gradient,linear-gradient(90deg,#4a90ff,#825bff));color:var(--on-accent);border:none}' +
        '.ptm-skip{background:transparent;color:var(--text-primary,#eef);border:1px solid var(--border,#303547)}' +
        '.ptm-cancel{background:transparent;color:var(--text-secondary,#aab);border:1px solid var(--border,#303547)}' +
        '.ptm-busy{display:flex;align-items:center;justify-content:center;gap:10px;padding:12px;color:var(--text-primary,#eef);font-size:14px}' +
        '.ptm-busy .ptm-spin{width:18px;height:18px;border:2px solid var(--border,#303547);border-top-color:var(--accent,#4a90ff);border-radius:50%;animation:ptm-spin 0.7s linear infinite}' +
        '@keyframes ptm-spin{to{transform:rotate(360deg)}}' +
        '.ptm-hidden{display:none!important}' +
        '@media(max-width:480px){.ptm-card{padding:16px}.ptm-title{font-size:16px}.ptm-video{max-height:140px}}';
    document.head.appendChild(st);
}

// ─── main API ─────────────────────────────────────────────────────────
async function show(file, opts) {
    opts = opts || {};
    if (!file || typeof file.size !== 'number') return file;
    var threshold = typeof opts.threshold === 'number' ? opts.threshold : DEFAULT_THRESHOLD;
    var defaultLen = typeof opts.defaultLen === 'number' ? opts.defaultLen : DEFAULT_LEN;
    var slug = opts.slug || '';
    if (file.size < threshold) return file;

    var duration = await probeDuration(file);
    if (!duration || !isFinite(duration)) {
        // Probing failed entirely — let the pipeline try the original. Better
        // than blocking on a metadata read we can't do.
        return file;
    }

    emit('pretrim_offered', { fileMB: Math.round(file.size / 1048576), durationSec: Math.round(duration) }, slug);

    return await new Promise(function(resolve) {
        ensureStyles();
        buildUI(file, duration, defaultLen, slug, resolve);
    });
}

function buildUI(file, duration, defaultLen, slug, resolve) {
    var overlay = document.createElement('div');
    overlay.className = 'ptm-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    var sizeMB = (file.size / 1048576).toFixed(0);
    var defaultEnd = Math.min(defaultLen, duration);
    overlay.innerHTML =
        '<div class="ptm-card">' +
          '<div class="ptm-title">' + tr('title') + '</div>' +
          '<div class="ptm-msg">' + tr('explain').replace('{size}', sizeMB).replace('{dur}', fmtTime(duration)) + '</div>' +
          '<video class="ptm-video" id="ptmVideo" muted playsinline preload="metadata"></video>' +
          '<div class="ptm-rangewrap">' +
            '<div class="ptm-track"></div>' +
            '<input type="range" class="ptm-range" id="ptmStart" min="0" max="' + Math.floor(duration*100) + '" step="1" value="0">' +
            '<input type="range" class="ptm-range" id="ptmEnd"   min="0" max="' + Math.floor(duration*100) + '" step="1" value="' + Math.floor(defaultEnd*100) + '">' +
          '</div>' +
          '<div class="ptm-times">' +
            '<span><b>' + tr('start') + ':</b> <span id="ptmStartLbl">0:00</span></span>' +
            '<span><b>' + tr('end') + ':</b> <span id="ptmEndLbl">' + fmtTime(defaultEnd) + '</span></span>' +
            '<span><b>' + tr('len') + ':</b> <span id="ptmLenLbl">' + fmtTime(defaultEnd) + '</span></span>' +
          '</div>' +
          '<div class="ptm-actions" id="ptmActions">' +
            '<button class="ptm-confirm" id="ptmConfirm">' + tr('confirm') + '</button>' +
            '<button class="ptm-skip" id="ptmSkip">' + tr('skip') + '</button>' +
            '<button class="ptm-cancel" id="ptmCancel">' + tr('cancel') + '</button>' +
          '</div>' +
          '<div class="ptm-busy ptm-hidden" id="ptmBusy">' +
            '<div class="ptm-spin"></div><span>' + tr('busy') + '</span>' +
          '</div>' +
        '</div>';
    document.body.appendChild(overlay);

    var url = URL.createObjectURL(file);
    var vid = overlay.querySelector('#ptmVideo');
    vid.src = url;
    var ptStart = overlay.querySelector('#ptmStart');
    var ptEnd = overlay.querySelector('#ptmEnd');
    var ptStartLbl = overlay.querySelector('#ptmStartLbl');
    var ptEndLbl = overlay.querySelector('#ptmEndLbl');
    var ptLenLbl = overlay.querySelector('#ptmLenLbl');

    function syncLabels() {
        var s = parseInt(ptStart.value) / 100;
        var e = parseInt(ptEnd.value) / 100;
        if (s >= e) { s = Math.max(0, e - 0.1); ptStart.value = Math.floor(s * 100); }
        ptStartLbl.textContent = fmtTime(s);
        ptEndLbl.textContent = fmtTime(e);
        ptLenLbl.textContent = fmtTime(e - s);
    }
    ptStart.addEventListener('input', function() { syncLabels(); try { vid.currentTime = parseInt(ptStart.value) / 100; } catch (_) {} });
    ptEnd.addEventListener('input', function() { syncLabels(); try { vid.currentTime = parseInt(ptEnd.value) / 100; } catch (_) {} });

    // Forward-declared so the wave-1 beforeunload telemetry block below can
    // hand it a cleanup hook. Reassigning `close` after the function
    // declaration here would only update some references depending on engine
    // semantics — safer to keep it a single declaration and have it remove
    // the unload listener itself.
    var _unloadListener = null;
    function close(result) {
        if (_unloadListener) { try { window.removeEventListener('beforeunload', _unloadListener); } catch (_) {} _unloadListener = null; }
        try { URL.revokeObjectURL(url); } catch (_) {}
        try { document.body.removeChild(overlay); } catch (_) {}
        resolve(result);
    }

    overlay.querySelector('#ptmCancel').addEventListener('click', function() {
        emit('pretrim_cancel', null, slug);
        close(null);
    });
    overlay.querySelector('#ptmSkip').addEventListener('click', function() {
        emit('pretrim_skip', { fileMB: Math.round(file.size / 1048576), durationSec: Math.round(duration) }, slug);
        close(file);
    });
    overlay.querySelector('#ptmConfirm').addEventListener('click', async function() {
        var s = parseInt(ptStart.value) / 100;
        var e = parseInt(ptEnd.value) / 100;
        if (e - s < 0.5) {
            if (window.__toast) window.__toast(tr('too_short'), 'warning');
            return;
        }
        /* The whole clip is not a trim. Rewriting it anyway hands the tool a NEW
           File, and on a phone the uploader copies every new File into the
           browser's storage — support #649: each pick on an iPad was copied as
           175 MB and then again as 175 MB for the «trimmed» copy of the very same
           bytes (pretrim_done 175 → 175), and the copies outlive the page. */
        if (parseInt(ptStart.value, 10) <= 0 && parseInt(ptEnd.value, 10) >= parseInt(ptEnd.max, 10)) {
            emit('pretrim_full_range', { fileMB: Math.round(file.size / 1048576) }, slug);
            close(file);
            return;
        }
        overlay.querySelector('#ptmBusy').classList.remove('ptm-hidden');
        overlay.querySelector('#ptmActions').classList.add('ptm-hidden');
        emit('pretrim_run', { startSec: Math.round(s), endSec: Math.round(e), fileMB: Math.round(file.size / 1048576) }, slug);
        var t0 = Date.now();
        try {
            var trimmed = await trimDispatch(file, s, e, slug);
            emit('pretrim_done', {
                fromMB: Math.round(file.size / 1048576),
                toMB:   Math.round(trimmed.size / 1048576),
                ms:     Date.now() - t0,
                via:    trimmed.__trimVia || 'unknown'
            }, slug);
            close(trimmed);
        } catch (err) {
            console.warn('[PreTrimModal] trim failed:', err && err.message);
            emit('pretrim_failed', { err: ((err && err.message) || '').substring(0, 100), ms: Date.now() - t0 }, slug);
            if (window.__toast) window.__toast(tr('err'), 'warning');
            close(file);
        }
    });
    // Wave 1 telemetry (2026-06-01): Escape used to silently close the modal
    // without firing a *_cancel or *_skip event — that's why pretrim_offered=103
    // didn't add up to skip+run+cancel on prod (the Escape-out cohort was the
    // missing slice). Treat Escape as an explicit pretrim_dismissed_esc.
    overlay.addEventListener('keydown', function(ev) {
        if (ev.key === 'Escape') {
            emit('pretrim_dismissed_esc', { fileMB: Math.round(file.size / 1048576) }, slug);
            close(null);
        }
    });
    // Tab close / page unload while the modal is open is the OTHER silent
    // path. Fire a beforeunload breadcrumb so we can size the
    // abandon-during-decide cohort. Listener is removed inside close() so a
    // normal flow doesn't leak listeners across multiple pretrim opens.
    var _unloadEmitted = false;
    _unloadListener = function() {
        if (_unloadEmitted) return;
        _unloadEmitted = true;
        try { emit('pretrim_abandoned_unload', { fileMB: Math.round(file.size / 1048576) }, slug); } catch (_) {}
        if (window.__aeFlush) try { window.__aeFlush(); } catch (_) {}
    };
    window.addEventListener('beforeunload', _unloadListener);
    setTimeout(function() { try { overlay.querySelector('#ptmConfirm').focus(); } catch (_) {} }, 50);
    syncLabels();
}

/* cut(file, startSec, endSec, slug) → Promise<File> — the same lossless
   dispatch the modal uses (Mp4CopyTrim → WebmCopyTrim → FFmpeg `-c copy`),
   without the modal: for a tool that has already decided the window itself
   (/video-dubbing's trial dubs the clip's first N seconds, 04.11.2026).
   The File carries `__trimVia`. Throws when no lane could cut. */
window.PreTrimModal = { show: show, cut: trimDispatch };

})();
